import React, { useState } from "react"
import { Modal, Alert } from "antd"
import { ExclamationCircleFilled } from "@ant-design/icons"

import Scheduling from "../../Apis/schedulingApi"
import {
  errorText,
  formatDate,
  formatTimeRange,
  parseSchedulingError,
} from "./schedulingUtils"
import "./Scheduling.css"

/**
 * Cancellation always names the attendee and the exact time before it acts.
 * "Cancelled" is only shown once Google confirms the deletion — an uncertain
 * answer stays "cancellation pending" and keeps the time blocked.
 */
const CancelMeetingModal = ({ booking, open, onClose, onCancelled, t, lang, timezone }) => {
  const [working, setWorking] = useState(false)
  const [error, setError] = useState("")

  if (!booking) return null

  const zone = timezone || booking.timezone

  const confirm = async () => {
    if (working) return
    try {
      setWorking(true)
      setError("")
      const response = await Scheduling.cancelBooking(booking.id)
      onCancelled?.(response?.data?.booking, response?.data?.status)
      onClose?.()
    } catch (caught) {
      const parsed = parseSchedulingError(caught)
      if (parsed.code === "cancellation_pending") {
        // Not a failure: the meeting stays blocked while we keep checking.
        onCancelled?.(null, "cancellation_pending")
        onClose?.()
        return
      }
      setError(errorText(t, parsed, t?.error_cancellation_failed))
    } finally {
      setWorking(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={working ? undefined : onClose}
      footer={null}
      width={520}
      centered
      closable={!working}
      title={
        <span className="sch-danger-title">
          <ExclamationCircleFilled /> {t?.cancel_confirm_title || "Cancel this meeting?"}
        </span>
      }
      className="scheduling-modal"
    >
      <p className="sch-muted">
        {t?.cancel_confirm_body || "Google Calendar will notify the attendee."}
      </p>

      <dl className="sch-detail-list">
        <div className="sch-detail-row">
          <dt className="sch-detail-label">{t?.detail_attendee || "Attendee"}</dt>
          <dd className="sch-detail-value">
            {booking.attendee_name} · <span dir="ltr">{booking.attendee_email}</span>
          </dd>
        </div>
        <div className="sch-detail-row">
          <dt className="sch-detail-label">{t?.detail_organizer_time || "Time"}</dt>
          <dd className="sch-detail-value">
            {formatDate(booking.start_at, zone, lang)}{" "}
            <span dir="ltr">{formatTimeRange(booking.start_at, booking.end_at, zone, lang)}</span>
          </dd>
        </div>
      </dl>

      <Alert
        type="info"
        showIcon
        className="sch-alert"
        message={
          t?.cancel_slot_note ||
          "The time stays unpublished afterwards. You can republish it later if you want it bookable again."
        }
      />

      {error ? <Alert type="error" showIcon message={error} className="sch-alert" /> : null}

      <div className="sch-modal-actions">
        <button
          type="button"
          className="sch-btn sch-btn-secondary"
          disabled={working}
          onClick={onClose}
        >
          {t?.keep_meeting || "Keep meeting"}
        </button>
        <button
          type="button"
          className="sch-btn sch-btn-danger"
          disabled={working}
          onClick={confirm}
        >
          {working ? t?.cancelling || "Cancelling…" : t?.cancel_meeting || "Cancel meeting"}
        </button>
      </div>
    </Modal>
  )
}

export default CancelMeetingModal
